import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  Input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { ControlContainer, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, map, Observable, startWith, switchMap } from 'rxjs';

import { ComparisonOperator } from '@blaze-shared/api-contracts-domain';
import {
  IntelliConfig,
  IntellisenseComponent,
  IntelliValueInfo,
} from '@blaze-shared/intellisense-feature';
import { SelectComponent } from '@blaze-shared/shared-feature';
import { SelectOption } from '@blaze-shared/shared-util';
import { CssMediaService } from '@blaze-shared/theming-data-access';

import { ComparisonQueryFormGroup, ComplexFilterRowColumn } from '../../complex-filter.models';
import {
  COMPLEX_FILTER_SERVICE,
  ComplexFilterService,
} from '../../providers/complex-filter.providers';

@UntilDestroy()
@Component({
  selector: '[formGroup] blaze-comparison-query-row, [formGroupName] blaze-comparison-query-row',
  imports: [AsyncPipe, FormsModule, ReactiveFormsModule, SelectComponent, IntellisenseComponent],
  templateUrl: './comparison-query-row.component.html',
  styleUrls: ['./comparison-query-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparisonQueryRowComponent implements OnInit {
  private readonly controlContainer = inject(ControlContainer);
  private readonly complexFilterService = inject<ComplexFilterService>(COMPLEX_FILTER_SERVICE);
  private readonly cssMediaService = inject(CssMediaService);

  operatorUpdate = output<ComparisonOperator>();

  @Input() isInOrganizeMode = false;
  @Input() disabled = false;
  @Input() @HostBinding('class.with-theme') useAppTheme = false;
  @Input() layout: 'row' | 'column' = 'row';
  @Input() @HostBinding('attr.data-level') level = 0;

  formGroup: FormGroup<ComparisonQueryFormGroup> | null = null;
  columns$: Observable<SelectOption<ComplexFilterRowColumn>[]> | null = null;
  operators$: Observable<SelectOption<ComparisonOperator>[]> | null = null;
  intelliConfig$: Observable<IntelliConfig> | null = null;

  columnErrors = signal<string[]>([]);
  valueErrors = signal<string[]>([]);

  isTouchMode = this.cssMediaService.isTouchMode;

  ngOnInit(): void {
    this.formGroup = this.controlContainer.control as FormGroup<ComparisonQueryFormGroup>;
    const valueChange$ = this.formGroup.valueChanges.pipe(startWith(this.formGroup.value));

    this.operators$ = this.complexFilterService.getOperators(valueChange$).pipe(
      map((operators) =>
        operators.map((operator) => ({
          value: operator.value,
          label: operator.label,
        })),
      ),
    );
    this.columns$ = this.complexFilterService.getColumns();
    this.intelliConfig$ = this.complexFilterService.getIntelliConfig(valueChange$);

    if (this.formGroup) {
      this.subscribeToErrorsChanges(this.formGroup);
    }

    this.formGroup.valueChanges.subscribe((formGroupValue) => {
      if (formGroupValue.operator) {
        this.operatorUpdate.emit(formGroupValue.operator);
      }
    });

    if (this.formGroup.value.operator) {
      this.operatorUpdate.emit(this.formGroup.value.operator);
    }
  }

  onIntelliValueChange(
    formGroup: FormGroup<ComparisonQueryFormGroup>,
    value: IntelliValueInfo,
  ): void {
    formGroup.patchValue({
      value: value.value,
      isFormulaValue: !!value.isFormulaValue,
      displayInfos: value.displayInfos,
    });
  }

  private subscribeToErrorsChanges(formGroup: FormGroup<ComparisonQueryFormGroup>): void {
    if (!this.complexFilterService.getErrors) {
      return;
    }

    const getErrors = this.complexFilterService.getErrors.bind(this.complexFilterService);

    formGroup.valueChanges
      .pipe(
        startWith(formGroup.value),
        switchMap((query) =>
          combineLatest({
            columnErrors: getErrors(query, 'column'),
            valueErrors: getErrors(query, 'value'),
          }),
        ),
        untilDestroyed(this),
      )
      .subscribe(({ columnErrors, valueErrors }) => {
        this.columnErrors.set(columnErrors);
        this.valueErrors.set(valueErrors);
      });
  }
}
